/**
 * BadgeCelebrationModal — MEM-BADGE-2C
 *
 * Premium in-place upgrade of the earned-badge celebration modal.
 *
 * Preserves:
 *   • shadcn Dialog (focus trap, ESC handling, aria-modal)
 *   • existing queue + Next/Done progression
 *   • existing dedupe (celebration NEVER decides which badge to show;
 *     that stays with useBadgeCelebrations.js)
 *   • badgeStyles.js as the ONLY source of rarity chrome
 *
 * Adds:
 *   • Federation Sports achievement header voice
 *   • Rarity-aware radial halo behind hero icon
 *   • Rarity-scaling motion (scale-in, ring pulse, shine sweep, particles)
 *   • Reduced-motion path — static premium presentation without any animation
 *   • Queue position ("1 of 3"), accessible via aria-live polite
 *   • "View Badge Center" secondary action (single-badge only)
 *   • aria-label on close, focus rings, semantic tokens
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog, DialogContent, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Sparkles, ChevronRight, X, Trophy, ArrowRight,
} from 'lucide-react';
import {
  Award, Vote, Star, Users, Calendar, MessageSquare,
  MessagesSquare, Heart, Activity, Crown, CheckCircle2, Flame,
  Hammer, Briefcase, ShieldCheck, Zap, Gift, Bookmark, Target, Compass,
  UsersRound, Binoculars, Eye, Megaphone, Anchor, ClipboardList,
  CalendarCheck, Share2, Pen, PlusSquare,
} from 'lucide-react';
import {
  getRarityStyle, getCelebrationStyle, RARITY_LABEL,
} from '../../lib/badgeStyles';

/* Same icon map as BadgeCard — celebrations must render the exact icon
 * the Member is unlocking. Kept here rather than imported to keep this
 * component self-contained + avoid any circular dep risk. */
const ICON_MAP = {
  vote: Vote, award: Award, star: Star, trophy: Trophy, users: Users,
  calendar: Calendar, message: MessageSquare, heart: Heart,
  activity: Activity, crown: Crown, check: CheckCircle2,
  sparkles: Sparkles, flame: Flame, hammer: Hammer, briefcase: Briefcase,
  shield: ShieldCheck, zap: Zap, gift: Gift, bookmark: Bookmark,
  target: Target, compass: Compass,
  'users-round': UsersRound, binoculars: Binoculars, eye: Eye,
  megaphone: Megaphone, messages: MessagesSquare, anchor: Anchor,
  'clipboard-list': ClipboardList, 'calendar-check': CalendarCheck,
  share: Share2, pen: Pen, 'plus-square': PlusSquare,
  'shield-star': ShieldCheck,
};
const resolveIcon = (key) => ICON_MAP[String(key || '').toLowerCase()] || Award;

/* Prefer reduced-motion — pure CSS media query is the source of truth,
 * this hook simply mirrors it into React so we can gate JS behaviors. */
const useReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = () => setReduced(!!mq.matches);
    handler();
    mq.addEventListener?.('change', handler);
    return () => mq.removeEventListener?.('change', handler);
  }, []);
  return reduced;
};

/* Component-scoped keyframes — appended once. Kept inline so the file
 * is self-contained and does not require a global CSS edit. */
const CELEBRATION_KEYFRAMES = `
@keyframes fed-badge-scale-in {
  0%   { transform: scale(0.6) rotate(-6deg); opacity: 0; }
  60%  { transform: scale(1.08) rotate(2deg); opacity: 1; }
  100% { transform: scale(1)    rotate(0);   opacity: 1; }
}
@keyframes fed-badge-ring-pulse {
  0%   { transform: scale(0.9); opacity: 0.8; }
  100% { transform: scale(1.5); opacity: 0; }
}
@keyframes fed-badge-shine-sweep {
  0%   { transform: translateX(-140%) skewX(-20deg); opacity: 0; }
  30%  { opacity: 0.9; }
  100% { transform: translateX(140%)  skewX(-20deg); opacity: 0; }
}
@keyframes fed-badge-halo {
  0%   { transform: scale(0.95); opacity: 0.6; }
  50%  { transform: scale(1.05); opacity: 0.85; }
  100% { transform: scale(0.95); opacity: 0.6; }
}
@keyframes fed-badge-particle {
  0%   { transform: translate(0,0)   scale(0.6); opacity: 0; }
  30%  { opacity: 1; }
  100% { transform: translate(var(--tx),var(--ty)) scale(0.2); opacity: 0; }
}
@keyframes fed-badge-check-in {
  0%   { transform: scale(0);    opacity: 0; }
  60%  { transform: scale(1.25); opacity: 1; }
  100% { transform: scale(1);    opacity: 1; }
}
`;

/* Emit the keyframes exactly once per session. */
let stylesInjected = false;
const injectStylesOnce = () => {
  if (stylesInjected || typeof document === 'undefined') return;
  const s = document.createElement('style');
  s.setAttribute('data-fed-badge-celebration', '');
  s.textContent = CELEBRATION_KEYFRAMES;
  document.head.appendChild(s);
  stylesInjected = true;
};

/* Tiny particle cluster — CSS-only, respects reduced motion via wrapper. */
const Particles = ({ tint }) => {
  // 8 small dots on a circle around the hero. Positions computed once.
  const dots = Array.from({ length: 8 }).map((_, i) => {
    const angle = (i / 8) * Math.PI * 2;
    const r = 90; // px offset
    const tx = Math.cos(angle) * r;
    const ty = Math.sin(angle) * r;
    return {
      key: i,
      style: {
        '--tx': `${tx}px`,
        '--ty': `${ty}px`,
        animation: `fed-badge-particle 1200ms ease-out ${i * 60}ms forwards`,
      },
    };
  });
  return (
    <div className="pointer-events-none absolute inset-0 flex items-center justify-center" aria-hidden="true">
      {dots.map((d) => (
        <span
          key={d.key}
          className={`absolute w-1.5 h-1.5 rounded-full ${tint}`}
          style={d.style}
        />
      ))}
    </div>
  );
};

const HeroBadge = ({ badge, reducedMotion }) => {
  const rarity  = getRarityStyle(badge.badge_rarity);
  const celeb   = getCelebrationStyle(badge.badge_rarity);
  const Icon    = resolveIcon(badge.icon);

  const animatedShine = celeb.shineSweep && !reducedMotion;
  const animatedPulse = celeb.ringPulse  && !reducedMotion;
  const animatedParticles = celeb.particles && !reducedMotion;

  return (
    <div className="relative w-40 h-40 mx-auto flex items-center justify-center" aria-hidden="true">
      {/* Radial halo */}
      <div
        className={`absolute inset-[-24px] ${celeb.haloClass} rounded-full`}
        style={reducedMotion ? undefined : { animation: 'fed-badge-halo 2400ms ease-in-out infinite' }}
      />
      {/* Expanding ring pulse — one shot */}
      {animatedPulse && (
        <div
          className={`absolute w-32 h-32 rounded-full border-2 ${rarity.ring}`}
          style={{ animation: 'fed-badge-ring-pulse 1400ms ease-out 200ms forwards' }}
        />
      )}
      {/* Particles */}
      {animatedParticles && <Particles tint={celeb.sparkleTint} />}

      {/* Icon disc */}
      <div
        className={[
          'relative w-28 h-28 rounded-full border flex items-center justify-center overflow-hidden',
          rarity.iconBg,
          rarity.ring,
          rarity.glow,
        ].join(' ')}
        style={reducedMotion ? undefined : { animation: 'fed-badge-scale-in 700ms cubic-bezier(0.22,1.2,0.36,1) both' }}
      >
        <Icon className={`w-12 h-12 ${rarity.icon}`} />
        {/* Shine sweep — clipped to disc */}
        {animatedShine && (
          <span
            className="absolute inset-y-0 w-1/2 bg-gradient-to-r from-transparent via-white/40 to-transparent"
            style={{ animation: 'fed-badge-shine-sweep 1100ms ease-in-out 400ms forwards' }}
          />
        )}
      </div>

      {/* Checkmark corner */}
      <div
        className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-emerald-500/90 border-2 border-black flex items-center justify-center"
        style={reducedMotion ? undefined : { animation: 'fed-badge-check-in 500ms ease-out 900ms both' }}
      >
        <CheckCircle2 className="w-4 h-4 text-black" aria-hidden="true" />
      </div>
    </div>
  );
};

const BadgeCelebrationModal = ({ open, badges, onClose }) => {
  const [idx, setIdx] = useState(0);
  const reducedMotion = useReducedMotion();
  const navigate = useNavigate();

  useEffect(() => { if (open) { setIdx(0); injectStylesOnce(); } }, [open]);

  if (!badges || badges.length === 0) return null;
  const current = badges[idx];
  if (!current) return null;

  const rarity      = getRarityStyle(current.badge_rarity);
  const celeb       = getCelebrationStyle(current.badge_rarity);
  const rarityLabel = RARITY_LABEL[current.badge_rarity] || 'Common';
  const isLast      = idx === badges.length - 1;
  const total       = badges.length;

  const goToBadgeCenter = () => {
    onClose();
    // Give the dialog exit transition a tick to complete before routing.
    setTimeout(() => navigate('/member/badges'), 200);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className={[
          'bg-zinc-950 max-w-md overflow-hidden p-0',
          'border-2',
          celeb.ringPulse ? rarity.ring : 'border-zinc-800',
          celeb.shineSweep ? rarity.glow : '',
        ].join(' ')}
        data-testid="badge-celebration-modal"
        data-rarity={current.badge_rarity}
      >
        {/* Ambient rarity backdrop */}
        <div
          className={`absolute inset-0 opacity-30 pointer-events-none ${celeb.haloClass}`}
          aria-hidden="true"
        />
        {/* Close */}
        <div className="absolute top-2 right-2 z-10">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close celebration"
            className="w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
            data-testid="celebration-close"
          >
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>

        {/* Header voice */}
        <div className="relative px-6 pt-6 pb-2 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-300 mb-1 flex items-center justify-center gap-1.5">
            <Trophy className="w-3 h-3" aria-hidden="true" />
            Federation Sports · Achievement
          </div>
          <DialogTitle className="font-heading text-3xl tracking-tight text-white leading-none">
            Badge Unlocked
          </DialogTitle>
          {/* Rarity chip (text conveys rarity — not color alone) */}
          <div className="mt-3 flex items-center justify-center gap-2">
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 border font-mono text-[10px] uppercase tracking-widest ${rarity.chip}`}
              data-testid="celebration-rarity"
            >
              <Sparkles className="w-3 h-3" aria-hidden="true" />
              {rarityLabel}
            </span>
          </div>
          <DialogDescription className="sr-only">
            {`${rarityLabel} tier badge unlocked: ${current.badge_name}. ${current.description || ''}`}
          </DialogDescription>
        </div>

        {/* Hero */}
        <div className="relative px-6 py-4">
          <HeroBadge badge={current} reducedMotion={reducedMotion} />
        </div>

        {/* Badge name + description */}
        <div className="relative px-6 pb-3 text-center">
          <h3
            className={`font-heading uppercase tracking-tight text-xl ${celeb.accentText}`}
            data-testid="celebration-badge-name"
          >
            {current.badge_name}
          </h3>
          {current.description && (
            <p className="mt-1.5 text-xs text-zinc-400 leading-snug max-w-xs mx-auto">
              {current.description}
            </p>
          )}
          {current.earned_date && (
            <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300">
              Earned · {new Date(current.earned_date).toLocaleDateString('en-US',
                { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>

        {/* Queue position — accessible + visible */}
        {total > 1 && (
          <div
            className="relative px-6 pb-1 text-center"
            aria-live="polite"
            data-testid="celebration-queue-position"
          >
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              {idx + 1} of {total}
            </span>
          </div>
        )}

        {/* Actions */}
        <div className="relative px-6 pb-5 pt-3 flex items-center justify-between gap-2 flex-wrap">
          {total === 1 ? (
            <button
              type="button"
              onClick={goToBadgeCenter}
              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-zinc-200 outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
              data-testid="celebration-view-center"
            >
              View Badge Center <ArrowRight className="w-3 h-3" aria-hidden="true" />
            </button>
          ) : (
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {isLast ? "That's the last one." : 'Tap Next to see what else you unlocked.'}
            </span>
          )}

          {isLast ? (
            <Button
              type="button"
              onClick={onClose}
              size="sm"
              className={`bg-amber-500/20 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30 outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70`}
              data-testid="celebration-done"
            >
              Done
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => setIdx((i) => i + 1)}
              size="sm"
              className="bg-amber-500/20 text-amber-200 border border-amber-500/40 hover:bg-amber-500/30 outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70"
              data-testid="celebration-next"
            >
              Next <ChevronRight className="w-3.5 h-3.5 ml-1" aria-hidden="true" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BadgeCelebrationModal;
